import { formatPrice, formatPriceRange } from "@/app/utilities/helper";
import {
    calculateEMI,
    EMI_DEFAULT_INTEREST_RATE,
    EMI_DEFAULT_TENURE,
    formatEmiCurrency,
    getDefaultDownPayment,
} from "@/lib/utils/emi";
import { getOnRoadPrice } from "@/utils/onRoadPrice";

export type EmiCalculatorDescription = {
    intro: string;
    sections: { title: string; items: { label: string; text: string }[] }[];
    compare: string;
};

const formatRupee = (value?: number) => (value ? `₹${formatEmiCurrency(value)}` : "");

const pickVariant = (variants: any[], pick: "min" | "max" | "base") => {
    const active = variants.filter((v) => v?.status !== "discontinued" && Number(v?.price) > 0);
    if (!active.length) return null;
    if (pick === "base") {
        return active.find((v) => v?.is_base_variant) || active.reduce((a, b) => (Number(a.price) < Number(b.price) ? a : b));
    }
    if (pick === "max") {
        return active.reduce((a, b) => (Number(a.price) > Number(b.price) ? a : b));
    }
    return active.reduce((a, b) => (Number(a.price) < Number(b.price) ? a : b));
};

const emiForVariant = (variant: any, locationInfo: any, interestRate: number, tenureMonths: number) => {
    const onRoad = getOnRoadPrice(variant, locationInfo)?.onRoadPrice || Number(variant?.price) || 0;
    const downPayment = getDefaultDownPayment(onRoad);
    const { emi, loanAmount } = calculateEMI({
        price: onRoad,
        downPayment,
        interestRate,
        tenureMonths,
    });
    return { onRoad, downPayment, emi, loanAmount };
};

export const buildEmiCalculatorDescription = ({
    model,
    variants,
    cityName,
    interestRate = EMI_DEFAULT_INTEREST_RATE,
    tenureMonths = EMI_DEFAULT_TENURE,
}: {
    model: any;
    variants: any[];
    cityName: string;
    interestRate?: number;
    tenureMonths?: number;
}): EmiCalculatorDescription => {
    const brand = model?.brand_name || model?.brand?.name || "";
    const modelName = model?.name || "";
    const fullName = `${brand} ${modelName}`.trim();
    const locationInfo = model?.locationInfo;

    const baseVariant = pickVariant(variants, "base");
    const topVariant = pickVariant(variants, "max");

    const baseStats = baseVariant ? emiForVariant(baseVariant, locationInfo, interestRate, tenureMonths) : null;

    const intro = baseStats
        ? `${fullName} EMI starts at just ${formatRupee(baseStats.emi)} per month over a ${tenureMonths} month tenure at ${interestRate}% interest, with a ${formatRupee(baseStats.downPayment)} down payment on a loan amount of ${formatRupee(baseStats.loanAmount)} and an on-road price of ${formatRupee(baseStats.onRoad)}. Use the ${fullName} EMI Calculator to plan your car loan instantly and adjust the tenure, interest rate, or down payment below to find a monthly EMI plan that fits your budget.`
        : `Use the ${fullName} EMI Calculator to plan your car loan instantly and adjust the tenure, interest rate, or down payment below to find a monthly EMI plan that fits your budget.`;

    const sections = [
        {
            title: `Key ${modelName} Loan Details:`,
            items: [
                {
                    label: "Interest Rate",
                    text: "Typically ranges from 8.5% to 12% per annum based on the lender.",
                },
                {
                    label: "Minimum Down Payment",
                    text: "Usually around 10% to 20% of the on-road price, depending on the lender and scheme.",
                },
                {
                    label: "Loan Tenure",
                    text: "Flexible options from 12 to 84 months.",
                },
                {
                    label: "EMI Starts From",
                    text: baseStats
                        ? `As low as ${formatRupee(baseStats.emi)} per month on a ${tenureMonths}-month tenure at ${interestRate}% interest.`
                        : `Calculated at ${interestRate}% interest over ${tenureMonths} months for your selected variant in ${cityName || "your city"}.`,
                },
            ],
        },
    ];

    let compare = "";
    if (baseVariant && topVariant && baseStats && baseVariant._id !== topVariant._id) {
        const topStats = emiForVariant(topVariant, locationInfo, interestRate, tenureMonths);
        const baseLabel = baseVariant?.name || "base variant";
        const topLabel = topVariant?.name || "top variant";
        compare = `The top variant ${modelName} ${topLabel} with a loan amount of ${formatRupee(topStats.loanAmount)} starts at an EMI of ${formatRupee(topStats.emi)} per month, while the base variant ${modelName} ${baseLabel} at ${formatRupee(baseStats.loanAmount)} begins at just ${formatRupee(baseStats.emi)} per month. Both are calculated at ${interestRate}% interest over ${tenureMonths} months. Check the table below to compare all variants and find the one that suits your budget.`;
    } else if (model?.variant_count) {
        compare = `EMI options are available across all ${model.variant_count} variants of the ${modelName}. Check the table below to compare variants and find the one that suits your budget.`;
    }

    return { intro, sections, compare };
};

const escapeHtml = (value: string) =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

export const emiCalculatorDescriptionToHtml = (description: EmiCalculatorDescription): string => {
    const parts: string[] = [];

    if (description.intro) {
        parts.push(`<p>${escapeHtml(description.intro)}</p>`);
    }

    for (const section of description.sections) {
        parts.push(`<p><strong>${escapeHtml(section.title)}</strong></p>`);
        parts.push("<ul>");
        for (const item of section.items) {
            parts.push(
                `<li><strong>${escapeHtml(item.label)}:</strong> ${escapeHtml(item.text)}</li>`,
            );
        }
        parts.push("</ul>");
    }

    if (description.compare) {
        parts.push(`<p>${escapeHtml(description.compare)}</p>`);
    }

    return parts.join("");
};

export const formatEmiOnRoadRange = (model: any, variants: any[]) => {
    const locationInfo = model?.locationInfo;
    let min = 0;
    let max = 0;
    for (const item of variants) {
        if (item?.status === "discontinued") continue;
        const onRoad = getOnRoadPrice(item, locationInfo)?.onRoadPrice || Number(item?.price) || 0;
        if (onRoad <= 0) continue;
        if (!min || onRoad < min) min = onRoad;
        if (onRoad > max) max = onRoad;
    }
    if (min > 0) {
        return String(formatPriceRange({ base: min, top: max || min })).replace(/₹\s+/g, "₹").replace(/\s+-\s+/g, " – ");
    }
    return String(formatPrice(model?.price_range?.base || 0, { includeUnit: true })).replace(/₹\s+/g, "₹");
};
